/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Resolving the Stripe customer for the selected team.
 *
 * `cur_user.stripe_cus_info` is fetched once during login and never refreshed,
 * so a customer created mid-session — a newly created team, or a team set up by
 * hand for an external customer — is invisible until the user signs in again.
 * Components that read only the cached array then resolve an empty customer id
 * and the payment flow fails with no explanation.
 *
 * `resolveStripeCustomerId` reads the cache first and falls back to asking
 * Stripe, so the miss self-heals rather than requiring a re-login.
 *
 * The lookup is passed in rather than imported so this module performs no I/O of
 * its own and can be exercised without the app's axios setup. Callers pass
 * `PaymentsAPI.getCustomerByEmail`.
 */

/** Shape of the customer-by-email lookup this module delegates to. */
export type CustomerFetcher = (email: string) => Promise<any>;

/** Cached-only lookup: the team's customer, matched on name, or null. */
export const findStripeCustomerForTeam = (
  stripeCusInfo: unknown,
  teamName?: string,
): any | null => {
  if (!teamName || !Array.isArray(stripeCusInfo)) {
    return null;
  }
  return (
    stripeCusInfo.find((customer: any) => customer?.name === teamName) ?? null
  );
};

/**
 * The team's Stripe customer id, consulting the cache first and Stripe second.
 *
 * Returns an empty string when the team genuinely has no customer, which callers
 * should treat as "billing is not set up for this team" rather than passing on to
 * an API — the server rejects that with a 400 explaining as much.
 */
export const resolveStripeCustomerId = async (
  stripeCusInfo: unknown,
  teamName: string | undefined,
  email: string | undefined,
  fetchByEmail: CustomerFetcher,
): Promise<string> => {
  const cached = findStripeCustomerForTeam(stripeCusInfo, teamName);
  if (cached?.id) {
    return String(cached.id);
  }

  if (!teamName || !email || typeof fetchByEmail !== 'function') {
    return '';
  }

  try {
    const res = await fetchByEmail(email);
    const fresh = findStripeCustomerForTeam(res?.result?.data, teamName);
    return fresh?.id ? String(fresh.id) : '';
  } catch (error) {
    // A lookup failure is reported by the caller's own error handling; returning
    // empty keeps behaviour identical to the pre-existing cache miss.
    console.error('Failed to resolve Stripe customer for team:', error);
    return '';
  }
};
